/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PJ Sandbox 白板の重なり並列（レーン）レイアウト計算。同一メンバー行で日付が重なるブロックを最大 4 本まで縦に分割して並列表示するためのレーン割当と、重なり数（同時実行数）の算出を提供する
 * @graph-connects none
 */
import type { SandboxBlock } from './types.js';

/** 1 行で許容する最大の重なり数（並列レーン数）。 @graph-connects none */
export const MAX_OVERLAP = 4;

/** ブロックのレーン割当結果（何番目のレーンか / そのクラスタの総レーン数）。 */
export interface BlockLane {
  /** レーンのインデックス（0 始まり）。 */
  lane: number;
  /** 同じ重なりクラスタの総レーン数（= 並列本数）。行高をこの数で分割する。 */
  laneCount: number;
}

/** 区間（開始日・跨ぎ日数）。重なり数の計算に使う最小情報。 */
export interface DayInterval {
  startDay: number;
  spanDays: number;
}

/**
 * 区間集合の最大同時重なり数を返す（端点は排他: [0,4) と [4,8) は重ならない）。
 * @graph-connects none
 */
export function maxConcurrency(intervals: DayInterval[]): number {
  const events: { day: number; delta: number }[] = [];
  for (const it of intervals) {
    events.push({ day: it.startDay, delta: 1 });
    events.push({ day: it.startDay + it.spanDays, delta: -1 });
  }
  // 同一日では終了(-1)を開始(+1)より先に処理し、接する区間を重なりに数えない。
  events.sort((a, b) => a.day - b.day || a.delta - b.delta);
  let current = 0;
  let max = 0;
  for (const e of events) {
    current += e.delta;
    if (current > max) max = current;
  }
  return max;
}

/**
 * 配置済みブロックをメンバー行ごとにレーン割当する。重なる区間は貪欲法で別レーンへ振り、
 * 連結した重なりクラスタ内では総レーン数（= 並列本数）を揃えて返す。
 * @graph-connects none
 */
export function assignLanes(blocks: SandboxBlock[]): Map<string, BlockLane> {
  const result = new Map<string, BlockLane>();

  // メンバー行ごとにグルーピング（未配置は対象外）。
  const byRow = new Map<number, SandboxBlock[]>();
  for (const b of blocks) {
    const p = b.placement;
    if (p === null) continue;
    const arr = byRow.get(p.rowIndex) ?? [];
    arr.push(b);
    byRow.set(p.rowIndex, arr);
  }

  for (const arr of byRow.values()) {
    const sorted = [...arr].sort(
      (a, b) =>
        (a.placement?.startDay ?? 0) - (b.placement?.startDay ?? 0) || a.id.localeCompare(b.id),
    );

    let clusterEnd = Number.NEGATIVE_INFINITY;
    let cluster: { id: string; lane: number }[] = [];
    let laneEnd: number[] = [];

    const flush = (): void => {
      if (cluster.length === 0) return;
      const laneCount = Math.max(...cluster.map((c) => c.lane)) + 1;
      for (const c of cluster) {
        result.set(c.id, { lane: c.lane, laneCount });
      }
      cluster = [];
      laneEnd = [];
    };

    for (const b of sorted) {
      const p = b.placement;
      if (p === null) continue;
      // 直前のクラスタと重ならない → クラスタ確定してレーン管理をリセット。
      if (p.startDay >= clusterEnd) {
        flush();
        clusterEnd = Number.NEGATIVE_INFINITY;
      }
      // 空いている最小レーンを探す（無ければ新規レーン）。
      let lane = laneEnd.findIndex((end) => end <= p.startDay);
      if (lane === -1) {
        lane = laneEnd.length;
        laneEnd.push(0);
      }
      laneEnd[lane] = p.startDay + p.spanDays;
      cluster.push({ id: b.id, lane });
      clusterEnd = Math.max(clusterEnd, p.startDay + p.spanDays);
    }
    flush();
  }

  return result;
}
